import { EditorState } from 'prosemirror-state'

import { getPaginationBreakdown } from '#choo-app/lib/editor/plugins/inlinePageBreaks'
import { getPaginationPages } from '#choo-app/lib/editor/plugins/pagination'
import { RevisionTracker } from '#choo-app/lib/editor/plugins/revisions'

export interface PageRevisionInfo {
  index: number
  hasActiveRevision: boolean
  hasRevision: boolean
}

interface PageRange {
  start: number
  end: number
}

/**
 * Determines which pages in a document have revisions.
 * @returns Array of 0-indexed page numbers that contain changes
 */
export const getRevisedPageNumbers = (
  editorState: EditorState,
  revisionTracker: RevisionTracker,
): PageRevisionInfo[] => {
  return getPageRanges(editorState).reduce<PageRevisionInfo[]>(
    (revisedPageNumbers, pageRange, index) => {
      const pageRevisionInfo = getPageRevisionInfo(
        revisionTracker,
        pageRange,
        index,
      )

      if (pageRevisionInfo.hasRevision) {
        revisedPageNumbers.push(pageRevisionInfo)
      }

      return revisedPageNumbers
    },
    [],
  )
}

const getPageRevisionInfo = (
  tracker: RevisionTracker,
  { start, end }: PageRange,
  index: number,
): PageRevisionInfo => {
  const changes = tracker.changesBetween(start, end)
  const deletions = tracker.deletions.between(start, end)
  const revisions = [...changes, ...deletions]
  const activeRevisionId = tracker.activeRevision.snapshot.id

  return {
    index,
    hasActiveRevision: revisions.some(
      (change) => change.revision === activeRevisionId,
    ),
    hasRevision: revisions.some((change) => change.revision !== null),
  }
}

const getPageRanges = (editorState: EditorState): PageRange[] => {
  const pages = getPaginationPages(editorState)
  if (pages) {
    return pages.map((page) => ({
      start: page.start,
      end: page.end,
    }))
  }

  // TODO: Remove when legacy pagination fully removed
  const breakdown = getPaginationBreakdown(editorState)
  if (!breakdown) {
    return []
  }

  return breakdown.decorationData.map((pageInfo) => ({
    start: pageInfo.startPos,
    end: pageInfo.endPos,
  }))
}
